import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, User, ChevronUp, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FbmStatCellGraded } from "@/components/FbmStat";

export default function ScoutPlayersPage() {
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [q, setQ] = useState("");
  const [filterLeagueId, setFilterLeagueId] = useState("");
  const [filterTeamId, setFilterTeamId] = useState("");
  const [loading, setLoading] = useState(true);
  const [fbm, setFbm] = useState({});
  const [sortBy, setSortBy] = useState("fbm_score");
  const [sortDir, setSortDir] = useState("desc");

  const load = async (query = "", teamId = "") => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (teamId) params.team_id = teamId;
      const [p, t, l] = await Promise.all([
        api.get("/players", { params }),
        api.get("/teams"),
        api.get("/leagues"),
      ]);
      setPlayers(p.data); setTeams(t.data); setLeagues(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = setTimeout(() => load(q, filterTeamId), 250);
    return () => clearTimeout(h);
  }, [q, filterTeamId]);

  const teamsInLeague = filterLeagueId
    ? teams.filter((t) => (t.league_ids || []).includes(filterLeagueId))
    : teams;
  const teamIdsInLeague = new Set(teamsInLeague.map((t) => t.id));
  const visiblePlayers = filterLeagueId && !filterTeamId
    ? players.filter((p) => (p.team_ids || []).some((tid) => teamIdsInLeague.has(tid)))
    : players;

  // Phase 11.9 — adaptive bulk fetch; refetch on ignore_friendlies change.
  useEffect(() => {
    const ids = visiblePlayers.map((p) => p.id);
    if (!ids.length) { setFbm({}); return; }
    const csv = ids.join(",");
    api.get(`/fbm/bulk`, { params: { ids: csv, mode: "adaptive" } })
      .then((r) => setFbm(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePlayers.length, q, filterTeamId, filterLeagueId, ignoreFriendlies]);

  // Phase 17 — scout's global Players page hides ALL rows until a filter
  // is active. Same gating variable used by admin's page but the default
  // empty state is different (no rows + hint, instead of "without data
  // only").
  const filtersActive = Boolean(q || filterLeagueId || filterTeamId);

  const sortedPlayers = useMemo(() => {
    if (!filtersActive) return [];
    const rows = visiblePlayers.map((p) => {
      const f = fbm[p.id] || {};
      return {
        ...p,
        _fbm: f,
        fbm_score: f.fbm_score ?? null,
        overall: f.overall ?? null,
        finishing: f.finishing ?? null,
        passing: f.passing ?? null,
        defending: f.defending ?? null,
        minutes_played: f.minutes_played ?? null,
      };
    });
    const getVal = (r) => sortBy === "full_name" ? (r.full_name || "") : r[sortBy];
    rows.sort((a, b) => {
      const av = getVal(a); const bv = getVal(b);
      const aNull = av == null || av === ""; const bNull = bv == null || bv === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (sortBy === "full_name") {
        return sortDir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      return sortDir === "asc" ? Number(av) - Number(bv) : Number(bv) - Number(av);
    });
    return rows;
  }, [visiblePlayers, fbm, sortBy, sortDir, filtersActive]);

  const onSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir(key === "full_name" ? "asc" : "desc"); }
  };

  return (
    <div data-testid="scout-players-page">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Roster · Current form</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">Players</h1>
        <p className="text-muted-foreground mt-2 max-w-lg">Adaptive FBM — isolation on each player&apos;s newest season (≥10 matches) or newest + previous combined.</p>
      </header>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players…" className="pl-9" data-testid="scout-players-search-input" />
        </div>
        <div className="space-y-1 w-[200px]">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">League</Label>
          <Select value={filterLeagueId || "__all__"} onValueChange={(v) => { setFilterLeagueId(v === "__all__" ? "" : v); setFilterTeamId(""); }}>
            <SelectTrigger data-testid="scout-players-filter-league-select"><SelectValue placeholder="All leagues" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All leagues</SelectItem>
              {leagues.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-[220px]">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Team</Label>
          <Select value={filterTeamId || "__all__"} onValueChange={(v) => setFilterTeamId(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="scout-players-filter-team-select"><SelectValue placeholder={filterLeagueId ? "All teams in league" : "All teams"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{filterLeagueId ? "All teams in league" : "All teams"}</SelectItem>
              {teamsInLeague.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.country})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(filterLeagueId || filterTeamId || q) && (
          <Button type="button" variant="ghost" onClick={() => { setQ(""); setFilterLeagueId(""); setFilterTeamId(""); }} data-testid="scout-players-filter-clear" className="h-10">
            Clear filters
          </Button>
        )}
      </div>

      {/* Phase 17 — view-mode caption (scout sees only matched players) */}
      <div
        className="text-xs text-muted-foreground mb-3"
        data-testid="scout-players-view-caption"
      >
        {filtersActive ? (
          <span data-testid="scout-players-view-caption-filtered">
            Showing {sortedPlayers.length} matching player{sortedPlayers.length === 1 ? "" : "s"}
          </span>
        ) : (
          <span data-testid="scout-players-view-caption-default">
            Use search or the filters above to find a player
          </span>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto shadow-sm">
        <Table data-testid="scout-players-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <ScoutTh label="Full name"  sortKey="full_name"      sortBy={sortBy} sortDir={sortDir} onClick={onSort} align="left" testId="scout-sort-name" />
              <TableHead className="px-4 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Team(s)</TableHead>
              <ScoutTh label="FBM"        sortKey="fbm_score"      sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="scout-sort-fbm" />
              <ScoutTh label="Ovr"        sortKey="overall"        sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="scout-sort-overall" />
              <ScoutTh label="Fin"        sortKey="finishing"      sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="scout-sort-finishing" />
              <ScoutTh label="Pas"        sortKey="passing"        sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="scout-sort-passing" />
              <ScoutTh label="Def"        sortKey="defending"      sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="scout-sort-defending" />
              <ScoutTh label="Min"        sortKey="minutes_played" sortBy={sortBy} sortDir={sortDir} onClick={onSort} testId="scout-sort-minutes" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="px-6 py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : !filtersActive ? (
              <TableRow><TableCell colSpan={8} className="px-6 py-20 text-center" data-testid="scout-players-empty-default">
                <Search className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <div className="font-display uppercase tracking-wider text-muted-foreground">Use search or the filters above to find a player</div>
              </TableCell></TableRow>
            ) : sortedPlayers.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="px-6 py-16 text-center">
                <User className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <div className="font-display uppercase tracking-wider">No players match</div>
              </TableCell></TableRow>
            ) : sortedPlayers.map((p) => {
              const f = p._fbm || {};
              return (
                <TableRow key={p.id} className="tr-fade border-border hover:bg-muted/30" data-testid={`scout-player-row-${p.id}`}>
                  <TableCell className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      <Link to={`/scout/players/${p.id}`} className="hover:underline" data-testid={`scout-player-link-${p.id}`}>{p.full_name}</Link>
                      {f.period_label && (
                        <span
                          data-testid={`scout-player-period-${p.id}`}
                          className={`text-[10px] uppercase tracking-[0.15em] rounded px-1.5 py-0.5 border ${
                            f.period_kind === "season+prev"
                              ? "border-amber-500/40 text-amber-500/80"
                              : "border-border text-muted-foreground"
                          }`}
                          title={
                            f.period_kind === "season+prev"
                              ? `Newest season has only ${f.match_count} match${f.match_count === 1 ? "" : "es"} in window — folded in previous season.`
                              : `Isolation on ${f.period_label} (${f.match_count ?? 0} matches).`
                          }
                        >
                          {f.period_label.replace(/(\d{2})(\d{2})\/(\d{2})/g, "$2/$3")}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(p.teams || []).length === 0
                        ? <span className="text-muted-foreground text-xs">—</span>
                        : p.teams.map((t) => <Badge key={t.id} variant="secondary" className="rounded-none uppercase tracking-wider text-[10px]">{t.name}</Badge>)}
                    </div>
                  </TableCell>
                  <TableCell className="px-2 py-3 text-right font-mono font-semibold text-foreground/85" data-testid={`scout-fbm-score-${p.id}`}>
                    {f.fbm_score == null ? <span className="text-muted-foreground">—</span> : (Math.ceil(Number(f.fbm_score) * 10) / 10).toFixed(1)}
                  </TableCell>
                  <TableCell className="px-2 py-3 text-right"><FbmStatCellGraded value={f.overall}   testId={`scout-fbm-overall-${p.id}`} /></TableCell>
                  <TableCell className="px-2 py-3 text-right"><FbmStatCellGraded value={f.finishing} testId={`scout-fbm-finishing-${p.id}`} /></TableCell>
                  <TableCell className="px-2 py-3 text-right"><FbmStatCellGraded value={f.passing}   testId={`scout-fbm-passing-${p.id}`} /></TableCell>
                  <TableCell className="px-2 py-3 text-right"><FbmStatCellGraded value={f.defending} testId={`scout-fbm-defending-${p.id}`} /></TableCell>
                  <TableCell className="px-2 py-3 text-right font-mono text-muted-foreground">{f.minutes_played ?? "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ScoutTh({ label, sortKey, sortBy, sortDir, onClick, align = "right", testId }) {
  const active = sortBy === sortKey;
  const padding = align === "right" ? "px-2" : "px-4";
  return (
    <TableHead
      className={`${padding} py-4 font-display uppercase tracking-[0.15em] text-[11px] cursor-pointer select-none ${align === "right" ? "text-right" : ""}`}
      onClick={() => onClick(sortKey)}
      data-testid={testId}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
        {label}
        {active
          ? (sortDir === "asc"
              ? <ChevronUp className="h-3.5 w-3.5" data-testid={`${testId}-asc`} />
              : <ChevronDown className="h-3.5 w-3.5" data-testid={`${testId}-desc`} />)
          : <ChevronDown className="h-3.5 w-3.5 opacity-25" />}
      </span>
    </TableHead>
  );
}
